import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Slider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  LinearProgress,
  IconButton,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { Mic, PlayArrow, Pause, Stop, CheckCircle, Replay, Delete, Headphones, GraphicEq, ArrowDownward } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import VoicetrackSegueEditor, { resolveVoicetrackStart } from './VoicetrackSegueEditor';

export default function VoicetrackMarkerRecorder({
  open,
  onClose,
  markerItem,
  allItems,
  markerIndex,
  onSave,
  assets = [],
  regions = [],
  selectedRegion = null,
  stationId = null,
  savedVoicetracks = [],
  apiUrl = '',
  onRefreshSaved = null,
  musicBedCategories = [],
  musicBedAssets = [],
}) {
  // State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [microphoneLevel, setMicrophoneLevel] = useState(100);
  const [currentMicLevel, setCurrentMicLevel] = useState(0);
  const [micConnected, setMicConnected] = useState(false);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [useAsSweeper, setUseAsSweeper] = useState(false);

  // Music bed state
  const [musicBedEnabled, setMusicBedEnabled] = useState(false);
  const [musicBedSelectionMode, setMusicBedSelectionMode] = useState('random_from_category');
  const [musicBedCategoryId, setMusicBedCategoryId] = useState('');
  const [musicBedAssetId, setMusicBedAssetId] = useState('');
  const [musicBedVolume, setMusicBedVolume] = useState(-12);
  
  // Region split recording state
  const [activeRegionTab, setActiveRegionTab] = useState(0); // 0 = shared, 1+ = regions
  const [regionRecordings, setRegionRecordings] = useState({}); // { regionId: { audio, duration } }
  const [playingSavedId, setPlayingSavedId] = useState(null); // ID of saved voicetrack currently playing
  const [replacingVtId, setReplacingVtId] = useState(null); // When re-recording to overwrite a saved VT

  // Segue editor state
  const [segueData, setSegueData] = useState(null); // captured from the segue editor for the new recording
  const [editingSegueVt, setEditingSegueVt] = useState(null); // saved VT being segue-edited
  const [editingSegueAudioUrl, setEditingSegueAudioUrl] = useState(null); // blob URL for the saved VT audio
  const [savingSegue, setSavingSegue] = useState(false);
  const editingSegueBlobRef = useRef(null);
  const editingSegueDraftRef = useRef(null);
  const savedAudioRef = useRef(null);
  const savedBlobUrlRef = useRef(null);
  const previewAudioUrlRef = useRef(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const previewAudioRef = useRef(null);
  const audioContextRef = useRef(null);

  // Calculate previous 3 and next 5 items
  const previousItems = allItems.slice(Math.max(0, markerIndex - 3), markerIndex);
  const nextItems = allItems.slice(markerIndex + 1, markerIndex + 1 + 5);

  // Immediate neighbours used by the segue editor and the play-order list.
  // previousItems / nextItems stay in playlist order (earliest first).
  const immediatePrevious = previousItems.length > 0 ? previousItems[previousItems.length - 1] : null;
  const immediateNext = nextItems[0] || null;
  const earlierItems = previousItems.slice(0, -1);
  const laterItems = nextItems.slice(1);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const beforeAccent = isLight ? '#b45309' : '#fbbf24';
  const nextAccent = isLight ? '#0e7490' : '#22d3ee';
  const vtAccent = isLight ? '#6d28d9' : '#d4a0ff';
  // Mixblock voicetracks are positioned by the beatmix engine at the configured
  // transition, so manual segue data must not override that placement.
  const isMixblockMarker = markerItem?.mixblock_id != null
    || markerItem?.mixblock_vt_after_position != null;

  // Load audio devices
  const loadAudioDevices = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        stream.getTracks().forEach(track => track.stop());
      });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(devices);
      
      const firstMic = devices.find(d => d.kind === 'audioinput' && d.deviceId);
      if (firstMic && !selectedMicrophone) {
        setSelectedMicrophone(firstMic.deviceId);
      }
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
    }
  }, [selectedMicrophone]);

  // Connect microphone with level monitoring
  const connectMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMicrophone ? { exact: selectedMicrophone } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false
        }
      });
      
      streamRef.current = stream;
      setMicConnected(true);
      
      // Set up audio level monitoring
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (streamRef.current && streamRef.current.active) {
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          const level = Math.round((average / 255) * 100);
          setCurrentMicLevel(level);
          requestAnimationFrame(updateLevel);
        }
      };
      
      updateLevel();
    } catch (error) {
      console.error('Failed to connect microphone:', error);
      alert('Failed to connect microphone. Please check permissions.');
    }
  }, [selectedMicrophone]);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!streamRef.current) {
      alert('Please connect microphone first');
      return;
    }
    
    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedAudio(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setPreviewAudioUrl(url);
      };
      
      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 0.1);
      }, 100);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Recording failed. Please try again.');
    }
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  }, []);

  // Play preview
  const playPreview = () => {
    if (previewAudioUrl && previewAudioRef.current) {
      previewAudioRef.current.play();
      setIsPlayingPreview(true);
    }
  };

  // Pause preview
  const pausePreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Build music bed settings object
  const buildMusicBedSettings = () => {
    if (!musicBedEnabled) return {};
    return {
      music_bed_enabled: true,
      music_bed_selection_mode: musicBedSelectionMode,
      music_bed_category_id: musicBedSelectionMode === 'random_from_category' && musicBedCategoryId ? parseInt(musicBedCategoryId) : null,
      music_bed_asset_id: musicBedSelectionMode === 'specific' && musicBedAssetId ? parseInt(musicBedAssetId) : null,
      music_bed_volume_db: musicBedVolume,
      sweep_over_intro: useAsSweeper,
    };
  };

  // Build segue settings (marker-aligned offsets + auto-duck) captured from the editor
  const buildSegueSettings = () => {
    if (isMixblockMarker || !segueData) return {};
    return {
      vt_position: segueData.vtStart,
      vt_gap: segueData.overlap_prev,
      next_lead: segueData.next_lead,
      segue: segueData,
    };
  };

  // Save voicetrack
  const handleSave = () => {
    if (recordedAudio) {
      // Determine which region this recording is for
      const currentRegionId = regions.length > 0 && activeRegionTab > 0
        ? regions[activeRegionTab - 1]?.id
        : selectedRegion;

      onSave(recordedAudio, {
        microphoneLevel,
        selectedMicrophone,
        duration: recordingTime,
        use_as_sweeper: useAsSweeper,
        region_id: currentRegionId || null,
        ...buildMusicBedSettings(),
        ...buildSegueSettings(),
      });

      // Store in regional recordings map if recording for a region
      if (currentRegionId) {
        setRegionRecordings(prev => ({
          ...prev,
          [currentRegionId]: { audio: recordedAudio, duration: recordingTime }
        }));
      }
    }
  };

  const handleSaveForRegion = (regionId) => {
    if (recordedAudio) {
      onSave(recordedAudio, {
        microphoneLevel,
        selectedMicrophone,
        duration: recordingTime,
        use_as_sweeper: useAsSweeper,
        region_id: regionId,
        replace_vt_id: replacingVtId || undefined,
        ...buildMusicBedSettings(),
        ...buildSegueSettings(),
      });

      setRegionRecordings(prev => ({
        ...prev,
        [regionId]: { audio: recordedAudio, duration: recordingTime }
      }));

      // Reset recording for next region
      setRecordedAudio(null);
      setRecordingTime(0);
      setReplacingVtId(null);
      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl);
        setPreviewAudioUrl(null);
      }
    }
  };

  // Open the segue editor for an already-saved voicetrack
  const openSegueEditorForSaved = async (vt) => {
    if (isMixblockMarker) return;
    try {
      if (savedAudioRef.current) savedAudioRef.current.pause();
      setPlayingSavedId(null);
      // Load the saved audio as a blob so the editor can decode it
      const res = await fetch(`${apiUrl}/api/voicetrack-enhanced/${vt.id}/audio`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to load audio: ${res.status}`);
      const blob = await res.blob();
      if (editingSegueBlobRef.current) URL.revokeObjectURL(editingSegueBlobRef.current);
      const blobUrl = URL.createObjectURL(blob);
      editingSegueBlobRef.current = blobUrl;
      setEditingSegueAudioUrl(blobUrl);
      setEditingSegueVt(vt);
    } catch (err) {
      console.error('Failed to open segue editor:', err);
      alert('Could not load this voicetrack for editing.');
    }
  };

  const closeSegueEditorForSaved = () => {
    setEditingSegueVt(null);
    if (editingSegueBlobRef.current) {
      URL.revokeObjectURL(editingSegueBlobRef.current);
      editingSegueBlobRef.current = null;
    }
    setEditingSegueAudioUrl(null);
  };

  // Persist segue settings for an already-saved voicetrack via PUT
  const saveSegueForSaved = async (segue) => {
    if (!editingSegueVt) return;
    setSavingSegue(true);
    try {
      let existing = editingSegueVt.ai_settings;
      if (typeof existing === 'string') {
        try { existing = JSON.parse(existing); } catch (_) { existing = {}; }
      }
      const ai_settings = { ...(existing || {}), segue };
      const res = await fetch(`${apiUrl}/api/voicetrack-enhanced/${editingSegueVt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ai_settings }),
      });
      if (!res.ok) throw new Error(res.statusText);
      onRefreshSaved?.();
      closeSegueEditorForSaved();
    } catch (err) {
      console.error('Failed to save segue:', err);
      alert('Failed to save segue settings.');
    } finally {
      setSavingSegue(false);
    }
  };

  // Parse a saved VT's stored segue settings into the editor's initial shape
  const parseSavedSegue = (vt) => {
    if (!vt) return null;
    let s = vt.ai_settings;
    if (typeof s === 'string') {
      try { s = JSON.parse(s); } catch (_) { return null; }
    }
    return s?.segue || null;
  };

  // Cleanup function
  const cleanup = useCallback(() => {
    stopRecording();
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (error) {
        console.warn('Error closing AudioContext:', error);
      }
      audioContextRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }, [stopRecording]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      cleanup();
      setRecordedAudio(null);
      setRecordingTime(0);
      setIsRecording(false);
      setMicConnected(false);
      setUseAsSweeper(false);
      setMusicBedEnabled(false);
      setMusicBedSelectionMode('random_from_category');
      setMusicBedCategoryId('');
      setMusicBedAssetId('');
      setMusicBedVolume(-12);
      setPlayingSavedId(null);
      setReplacingVtId(null);
      setSegueData(null);
      setEditingSegueVt(null);
      if (editingSegueBlobRef.current) {
        URL.revokeObjectURL(editingSegueBlobRef.current);
        editingSegueBlobRef.current = null;
      }
      setEditingSegueAudioUrl(null);
      if (savedAudioRef.current) {
        savedAudioRef.current.pause();
        savedAudioRef.current = null;
      }
      if (savedBlobUrlRef.current) {
        URL.revokeObjectURL(savedBlobUrlRef.current);
        savedBlobUrlRef.current = null;
      }
      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl);
        setPreviewAudioUrl(null);
      }
    }
  }, [open, previewAudioUrl, cleanup]);

  // Keep ref in sync for cleanup on unmount
  useEffect(() => {
    previewAudioUrlRef.current = previewAudioUrl;
  }, [previewAudioUrl]);

  // Cleanup on unmount only (not when previewAudioUrl changes - that would stop the stream
  // when saving for a region and moving to the next, leaving mic "connected" but unusable)
  useEffect(() => {
    return () => {
      cleanup();
      if (previewAudioUrlRef.current) {
        URL.revokeObjectURL(previewAudioUrlRef.current);
      }
    };
  }, [cleanup]);

  // Load devices when modal opens
  useEffect(() => {
    if (open) {
      loadAudioDevices();
    }
  }, [open, loadAudioDevices]);

  // Automatically connect microphone when modal opens and microphone is selected
  useEffect(() => {
    if (open && selectedMicrophone && !micConnected) {
      // Small delay to ensure devices are loaded
      const timer = setTimeout(() => {
        connectMicrophone();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, selectedMicrophone, micConnected, connectMicrophone]);

  // Get item display name
  const getItemDisplayName = (item) => {
    if (item.type === 'mixblock') {
      return item.mixblockTitle || item.title || "Mixblock";
    }
    return item.title || item.asset?.title || item.metadata?.name || "Unknown Item";
  };

  // Get item artist/subtitle
  const getItemSubtitle = (item) => {
    if (item.type === 'mixblock') {
      return `${item.tracks?.length || 0} tracks`;
    }
    return item.artist || item.asset?.artist || "";
  };

  // Get category info for an item (matching main Voicetracking page)
  const getCategoryInfo = (item) => {
    let categoryName = 'Unknown';
    let categoryColor = '#666666';

    // Check if category is directly on the item
    if (item.category) {
      if (typeof item.category === 'object' && item.category !== null) {
        if (item.category.name) {
          categoryName = item.category.name;
          categoryColor = item.category.color || '#666666';
        }
      }
    }
    
    // Check if categories array exists
    if (categoryName === 'Unknown' && item.categories && Array.isArray(item.categories) && item.categories.length > 0) {
      const firstCategory = item.categories[0];
      if (firstCategory && typeof firstCategory === 'object' && firstCategory.name) {
        categoryName = firstCategory.name;
        categoryColor = firstCategory.color || '#666666';
      }
    }
    
    // Try to find category from assets list
    else if (item.asset_id) {
      const assetId = typeof item.asset_id === 'string' ? parseInt(item.asset_id, 10) : item.asset_id;
      if (!isNaN(assetId) && assets.length > 0) {
        const asset = assets.find(a => {
          const aId = typeof a.id === 'string' ? parseInt(a.id, 10) : a.id;
          return aId === assetId;
        });
        if (asset && asset.categories && Array.isArray(asset.categories) && asset.categories.length > 0) {
          categoryName = asset.categories[0].name || 'Unknown';
          categoryColor = asset.categories[0].color || '#666666';
        }
      }
    }
    
    // Special types
    if (item.type === 'commercial_break') {
      categoryName = 'Commercial';
      categoryColor = '#ff9800';
    } else if (item.type === 'voicetrack' || item.type === 'voice') {
      categoryName = 'Voicetrack';
      categoryColor = '#9c27b0';
    } else if (item.type === 'ai_voicetrack') {
      categoryName = 'AI Voicetrack';
      categoryColor = '#9c27b0';
    } else if (item.type === 'stream_relay') {
      categoryName = 'Stream Relay';
      categoryColor = '#607d8b';
    }

    return { categoryName, categoryColor };
  };

  const chipTextColor = (hex) => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4)) {
      return '#ffffff';
    }
    const full = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    const r = parseInt(full.slice(1, 3), 16);
    const g = parseInt(full.slice(3, 5), 16);
    const b = parseInt(full.slice(5, 7), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return '#ffffff';
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.64 ? '#0f172a' : '#ffffff';
  };

  const renderCompactItem = (item, key) => {
    const { categoryName, categoryColor } = getCategoryInfo(item);
    const subtitle = getItemSubtitle(item);
    return (
      <Box
        key={item.id || key}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          bgcolor: 'var(--rs-row)',
          border: '1px solid var(--rs-border)',
          minWidth: 0,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: categoryColor, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ color: 'var(--rs-text)', fontWeight: 500 }}>
            {getItemDisplayName(item)}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" noWrap sx={{ color: 'var(--rs-muted)', display: 'block' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Chip
          label={categoryName}
          size="small"
          sx={{
            bgcolor: categoryColor,
            color: chipTextColor(categoryColor),
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 140,
            flexShrink: 0,
          }}
        />
      </Box>
    );
  };

  const renderAnchorItem = (item, { kicker, accent, emptyText, hint }) => {
    if (!item) {
      return (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            border: '1px dashed var(--rs-border)',
            bgcolor: 'var(--rs-paper)',
          }}
        >
          <Typography sx={{ color: accent, fontSize: 12, fontWeight: 800, letterSpacing: 1.1 }}>
            {kicker}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mt: 0.5 }}>
            {emptyText}
          </Typography>
        </Box>
      );
    }

    const { categoryName, categoryColor } = getCategoryInfo(item);
    const subtitle = getItemSubtitle(item);
    const durationLabel = item.duration ? formatTime(item.duration) : '';
    const meta = [subtitle, durationLabel].filter(Boolean).join(' · ');

    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 1.5,
          bgcolor: 'var(--rs-paper)',
          border: '1px solid var(--rs-border)',
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
          <Typography sx={{ color: accent, fontSize: 12, fontWeight: 800, letterSpacing: 1.1 }}>
            {kicker}
          </Typography>
          <Chip
            label={categoryName}
            size="small"
            sx={{
              bgcolor: categoryColor,
              color: chipTextColor(categoryColor),
              fontSize: '0.65rem',
              height: 20,
              maxWidth: 160,
            }}
          />
        </Box>
        <Typography sx={{ color: 'var(--rs-text)', fontWeight: 650, fontSize: 16, lineHeight: 1.3 }}>
          {getItemDisplayName(item)}
        </Typography>
        {meta ? (
          <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mt: 0.25 }}>
            {meta}
          </Typography>
        ) : null}
        <Typography variant="caption" sx={{ color: 'var(--rs-muted)', display: 'block', mt: 0.75 }}>
          {hint}
        </Typography>
      </Box>
    );
  };

  const renderSequenceArrow = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', color: 'var(--rs-muted)', py: 0.25 }}>
      <ArrowDownward sx={{ fontSize: 18 }} />
    </Box>
  );

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Mic sx={{ color: '#22d3ee' }} />
        Record Voicetrack
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' }}>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Play order: earlier → before → voicetrack → next → later */}
          <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid var(--rs-border)' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: nextAccent, mb: 0.5 }}>
                Play order
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--rs-muted)', display: 'block', mb: 2 }}>
                Top to bottom is what the listener hears. Your voicetrack sits between the track before it and the track after it.
              </Typography>

              {earlierItems.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'var(--rs-muted)', fontWeight: 700, letterSpacing: 0.6 }}>
                    EARLIER
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
                    {earlierItems.map((item, idx) => renderCompactItem(item, `earlier-${idx}`))}
                  </Box>
                </Box>
              )}

              {renderAnchorItem(immediatePrevious, {
                kicker: 'BEFORE',
                accent: beforeAccent,
                emptyText: 'Nothing is scheduled before this voicetrack.',
                hint: (() => {
                  const placed = resolveVoicetrackStart(immediatePrevious, null);
                  if (placed.prevOutroRel == null) {
                    return 'Plays first. Your voicetrack starts when this track ends.';
                  }
                  return `Outro at ${formatTime(placed.prevSpan + placed.prevOutroRel)}. Your voicetrack starts there.`;
                })(),
              })}
              {renderSequenceArrow()}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: isLight ? 'rgba(109, 40, 217, 0.08)' : 'rgba(124, 58, 237, 0.22)',
                  border: `1px solid ${isLight ? 'rgba(109, 40, 217, 0.35)' : 'rgba(192, 96, 255, 0.55)'}`,
                }}
              >
                <Mic sx={{ color: vtAccent }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: vtAccent, fontWeight: 700, lineHeight: 1.3 }}>
                    Your voicetrack
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--rs-muted)' }}>
                    {immediatePrevious && immediateNext
                      ? `After “${getItemDisplayName(immediatePrevious)}”, before “${getItemDisplayName(immediateNext)}”`
                      : immediatePrevious
                        ? `After “${getItemDisplayName(immediatePrevious)}”`
                        : immediateNext
                          ? `Before “${getItemDisplayName(immediateNext)}”`
                          : 'Plays in this gap'}
                  </Typography>
                </Box>
              </Box>
              {renderSequenceArrow()}
              {renderAnchorItem(immediateNext, {
                kicker: 'NEXT',
                accent: nextAccent,
                emptyText: 'Nothing is scheduled after this voicetrack.',
                hint: 'Starts once your voicetrack has played.',
              })}

              {laterItems.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'var(--rs-muted)', fontWeight: 700, letterSpacing: 0.6 }}>
                    LATER
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
                    {laterItems.map((item, idx) => renderCompactItem(item, `later-${idx}`))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Region Split Recording Tabs */}
          {regions.length > 0 && (
            <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                  Split Voicetrack Recording
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    Record separate voicetracks for each region. Select a region tab below, record, 
                    then save for that region before moving to the next.
                  </Typography>
                </Alert>
                <Tabs 
                  value={activeRegionTab} 
                  onChange={(e, v) => setActiveRegionTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    mb: 1,
                    '& .MuiTab-root': { color: '#999', minHeight: 36, py: 0 },
                    '& .Mui-selected': { color: '#22d3ee' },
                    '& .MuiTabs-indicator': { bgcolor: '#22d3ee' },
                  }}
                >
                  <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Shared (All)
                      {savedVoicetracks.some(vt => !vt.region_id) && (
                        <CheckCircle sx={{ fontSize: 14, color: '#4caf50' }} />
                      )}
                    </Box>
                  }
                />
                  {regions.map((region) => {
                    const hasRecording = regionRecordings[region.id];
                    const hasSaved = savedVoicetracks.some(vt => vt.region_id === region.id);
                    const hasEither = hasRecording || hasSaved;
                    return (
                      <Tab 
                        key={region.id} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {region.name}
                            {hasEither && (
                              <CheckCircle sx={{ fontSize: 14, color: '#4caf50' }} />
                            )}
                          </Box>
                        } 
                      />
                    );
                  })}
                </Tabs>
                
                {activeRegionTab > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#1a1a3a', borderRadius: 1 }}>
                    <Chip 
                      label={regions[activeRegionTab - 1]?.name || 'Region'} 
                      size="small" 
                      sx={{ bgcolor: '#22d3ee20', color: '#22d3ee' }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      {replacingVtId
                        ? 'Re-recording will replace the existing voicetrack for this region.'
                        : 'Recording will be saved for this region only. Use the "Save for Region" button.'}
                    </Typography>
                  </Box>
                )}
                
                {/* Region recordings summary - show saved VTs and in-memory recordings */}
                {(Object.keys(regionRecordings).length > 0 || savedVoicetracks.some(vt => vt.region_id)) && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {regions.map(r => {
                      const hasRecording = regionRecordings[r.id];
                      const hasSaved = savedVoicetracks.some(vt => vt.region_id === r.id);
                      const status = hasRecording ? '✓ Recorded' : hasSaved ? '✓ Saved' : 'Pending';
                      return (
                        <Chip
                          key={r.id}
                          label={`${r.name}: ${status}`}
                          size="small"
                          sx={{
                            bgcolor: (hasRecording || hasSaved) ? '#4caf5020' : '#66620',
                            color: (hasRecording || hasSaved) ? '#4caf50' : '#999',
                            fontSize: '0.7rem',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Saved recordings - listen to each recorded voicetrack */}
          {savedVoicetracks.length > 0 && (
            <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456', mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                  Saved recordings – Listen
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Play back any saved voicetrack for this marker.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {savedVoicetracks.map((vt) => {
                    const regionName = vt.region_id && regions.length
                      ? (regions.find(r => r.id === vt.region_id)?.name || `Region ${vt.region_id}`)
                      : 'Shared';
                    const isPlaying = playingSavedId === vt.id;
                    const duration = vt.duration ? `${Math.floor(vt.duration / 60)}:${String(Math.floor(vt.duration % 60)).padStart(2, '0')}` : '—';
                    return (
                      <Box
                        key={vt.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          bgcolor: 'var(--rs-paper)',
                          borderRadius: 1,
                          border: '1px solid var(--rs-border)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={regionName} size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.7rem' }} />
                          <Typography variant="body2" sx={{ color: '#ccc' }}>{duration}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={async () => {
                              if (isPlaying) {
                                if (savedAudioRef.current) savedAudioRef.current.pause();
                                if (savedBlobUrlRef.current) {
                                  URL.revokeObjectURL(savedBlobUrlRef.current);
                                  savedBlobUrlRef.current = null;
                                }
                                setPlayingSavedId(null);
                              } else {
                                if (savedAudioRef.current) savedAudioRef.current.pause();
                                if (savedBlobUrlRef.current) {
                                  URL.revokeObjectURL(savedBlobUrlRef.current);
                                  savedBlobUrlRef.current = null;
                                }
                                const url = `${apiUrl}/api/voicetrack-enhanced/${vt.id}/audio`;
                                try {
                                  const res = await fetch(url, { credentials: 'include' });
                                  if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
                                  const blob = await res.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  savedBlobUrlRef.current = blobUrl;
                                  const audio = new Audio(blobUrl);
                                  audio.addEventListener('ended', () => {
                                    if (savedBlobUrlRef.current) {
                                      URL.revokeObjectURL(savedBlobUrlRef.current);
                                      savedBlobUrlRef.current = null;
                                    }
                                    setPlayingSavedId(null);
                                  });
                                  audio.addEventListener('error', () => {
                                    if (savedBlobUrlRef.current) {
                                      URL.revokeObjectURL(savedBlobUrlRef.current);
                                      savedBlobUrlRef.current = null;
                                    }
                                    setPlayingSavedId(null);
                                  });
                                  savedAudioRef.current = audio;
                                  await audio.play();
                                  setPlayingSavedId(vt.id);
                                } catch (err) {
                                  console.error('Voicetrack playback failed:', err);
                                  setPlayingSavedId(null);
                                }
                              }
                            }}
                            sx={{ color: isPlaying ? '#22d3ee' : '#4caf50' }}
                            title={isPlaying ? 'Pause' : 'Play'}
                          >
                            {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                          </IconButton>
                          {vt.region_id && (
                            <Tooltip title="Re-record to replace this voicetrack">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (savedAudioRef.current) savedAudioRef.current.pause();
                                  setPlayingSavedId(null);
                                  const idx = regions.findIndex(r => r.id === vt.region_id);
                                  if (idx >= 0) setActiveRegionTab(idx + 1);
                                  setReplacingVtId(vt.id);
                                  setRecordedAudio(null);
                                  setRecordingTime(0);
                                  if (previewAudioUrl) {
                                    URL.revokeObjectURL(previewAudioUrl);
                                    setPreviewAudioUrl(null);
                                  }
                                }}
                                sx={{ color: '#ff9800' }}
                              >
                                <Replay fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!isMixblockMarker && (
                            <Tooltip title="Edit segue & volumes">
                              <IconButton
                                size="small"
                                onClick={() => openSegueEditorForSaved(vt)}
                                sx={{ color: '#c060ff' }}
                              >
                                <GraphicEq fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete this recording">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                if (!window.confirm(`Delete the ${regionName} voicetrack recording?`)) return;
                                if (playingSavedId === vt.id) {
                                  if (savedAudioRef.current) savedAudioRef.current.pause();
                                  if (savedBlobUrlRef.current) {
                                    URL.revokeObjectURL(savedBlobUrlRef.current);
                                    savedBlobUrlRef.current = null;
                                  }
                                  setPlayingSavedId(null);
                                }
                                try {
                                  const res = await fetch(`${apiUrl}/api/voicetrack-enhanced/${vt.id}`, {
                                    method: 'DELETE',
                                    credentials: 'include'
                                  });
                                  if (!res.ok) throw new Error(res.statusText);
                                  onRefreshSaved?.();
                                } catch (err) {
                                  console.error('Failed to delete voicetrack:', err);
                                  alert('Failed to delete recording');
                                }
                              }}
                              sx={{ color: '#ff6b6b' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Options - always visible */}
          <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 2 }}>
                Options
              </Typography>
              <Tooltip title="Sweepers play over the intro of the next track. Use for dry voiceovers over music intros.">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useAsSweeper}
                      onChange={(e) => setUseAsSweeper(e.target.checked)}
                      sx={{ color: '#22d3ee', '&.Mui-checked': { color: '#22d3ee' } }}
                    />
                  }
                  label="Sweep Over Intro"
                  sx={{ color: 'var(--rs-text)' }}
                />
              </Tooltip>

              <Divider sx={{ borderColor: '#3a4456', my: 2 }} />

              {/* Music Bed Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Headphones sx={{ color: '#22d3ee', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ color: '#22d3ee' }}>
                  Music Bed
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                Add a music bed underneath your voicetrack. It will be mixed down automatically when you save.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={musicBedEnabled}
                    onChange={(e) => setMusicBedEnabled(e.target.checked)}
                    sx={{ color: '#22d3ee', '&.Mui-checked': { color: '#22d3ee' } }}
                  />
                }
                label="Enable Music Bed"
                sx={{ color: 'var(--rs-text)', mb: 1 }}
              />

              {musicBedEnabled && (
                <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: 'var(--rs-muted)' }}>Selection Mode</InputLabel>
                    <Select
                      value={musicBedSelectionMode}
                      onChange={(e) => {
                        setMusicBedSelectionMode(e.target.value);
                        setMusicBedCategoryId('');
                        setMusicBedAssetId('');
                      }}
                      label="Selection Mode"
                      sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                    >
                      <MenuItem value="random_from_category">Random from Sub-Category</MenuItem>
                      <MenuItem value="random_any">Random Any Music Bed</MenuItem>
                      <MenuItem value="specific">Specific Music Bed</MenuItem>
                    </Select>
                  </FormControl>

                  {musicBedSelectionMode === 'random_from_category' && (
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ color: 'var(--rs-muted)' }}>Music Bed Category</InputLabel>
                      <Select
                        value={musicBedCategoryId}
                        onChange={(e) => setMusicBedCategoryId(e.target.value)}
                        label="Music Bed Category"
                        sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                      >
                        {musicBedCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.parent_category_id ? `  └ ${cat.name}` : cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {musicBedSelectionMode === 'specific' && (
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ color: 'var(--rs-muted)' }}>Select Music Bed</InputLabel>
                      <Select
                        value={musicBedAssetId}
                        onChange={(e) => setMusicBedAssetId(e.target.value)}
                        label="Select Music Bed"
                        sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                      >
                        {musicBedAssets.map((asset) => (
                          <MenuItem key={asset.id} value={asset.id}>
                            {asset.title} {asset.artist ? `- ${asset.artist}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    label="Music Bed Volume (dB)"
                    type="number"
                    size="small"
                    value={musicBedVolume}
                    onChange={(e) => {
                      const parsed = parseFloat(e.target.value);
                      setMusicBedVolume(Number.isFinite(parsed) ? parsed : -12);
                    }}
                    inputProps={{ step: 1, min: -40, max: 0 }}
                    helperText="e.g. -12 dB underneath voice"
                    sx={{
                      '& .MuiInputBase-root': { color: 'var(--rs-text)' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' },
                      '& .MuiInputLabel-root': { color: 'var(--rs-muted)' },
                      '& .MuiFormHelperText-root': { color: '#666' },
                    }}
                  />

                  {musicBedEnabled && useAsSweeper && (
                    <Alert severity="info" sx={{ bgcolor: '#1a2a3a', color: '#8ecfff' }}>
                      <Typography variant="caption">
                        Music bed + sweep over intro: The music bed will play under your voicetrack, 
                        then sweep under the intro of the next song automatically.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Microphone Setup */}
          <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 2 }}>
                Microphone Setup
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'var(--rs-text)' }}>Microphone</InputLabel>
                  <Select
                    value={selectedMicrophone}
                    onChange={(e) => setSelectedMicrophone(e.target.value)}
                    label="Microphone"
                    sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                  >
                    {audioDevices
                      .filter(device => device.kind === 'audioinput' && device.deviceId)
                      .map(device => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${device.deviceId.slice(0, 4)}`}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                
                <Button
                  onClick={connectMicrophone}
                  disabled={!selectedMicrophone || micConnected}
                  variant="contained"
                  sx={{
                    bgcolor: micConnected ? '#4caf50' : '#22d3ee',
                    '&:hover': { bgcolor: micConnected ? '#45a049' : '#00b8e6' }
                  }}
                >
                  {micConnected ? '✓ Microphone Connected' : 'Connect Microphone'}
                </Button>

                {micConnected && (
                  <Box>
                    <Typography variant="body2" sx={{ color: 'var(--rs-text)', mb: 1 }}>
                      Microphone Level: {microphoneLevel}%
                    </Typography>
                    <Slider
                      value={microphoneLevel}
                      onChange={(e, value) => setMicrophoneLevel(value)}
                      min={0}
                      max={100}
                      sx={{ color: '#22d3ee' }}
                    />
                    <Box sx={{ mt: 1, height: 8, bgcolor: 'var(--rs-border)', borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          bgcolor: '#22d3ee',
                          width: `${currentMicLevel}%`,
                          transition: 'width 0.1s'
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Recording Controls */}
          <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 2 }}>
                Recording
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {!isRecording && !recordedAudio && (
                  <Button
                    onClick={startRecording}
                    disabled={!micConnected}
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: '#ff6b6b',
                      '&:hover': { bgcolor: '#ff5252' },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    <Mic sx={{ mr: 1 }} />
                    Start Recording
                  </Button>
                )}
                
                {isRecording && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#ff6b6b', borderRadius: '50%', animation: 'pulse 1s infinite' }} />
                      <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#ff6b6b' }}>
                        {formatTime(recordingTime)}
                      </Typography>
                    </Box>
                    <Button
                      onClick={stopRecording}
                      variant="contained"
                      sx={{ bgcolor: '#666', '&:hover': { bgcolor: '#777' } }}
                    >
                      Stop Recording
                    </Button>
                  </Box>
                )}

                {recordedAudio && !isRecording && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Chip label={`Recording Complete: ${formatTime(recordingTime)}`} sx={{ bgcolor: '#4caf50', color: '#fff' }} />
                    {previewAudioUrl && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isPlayingPreview ? (
                          <IconButton onClick={pausePreview} sx={{ color: '#22d3ee' }}>
                            <Pause />
                          </IconButton>
                        ) : (
                          <IconButton onClick={playPreview} sx={{ color: '#22d3ee' }}>
                            <PlayArrow />
                          </IconButton>
                        )}
                        <audio
                          ref={previewAudioRef}
                          src={previewAudioUrl}
                          onEnded={() => setIsPlayingPreview(false)}
                          onPause={() => setIsPlayingPreview(false)}
                          onPlay={() => setIsPlayingPreview(true)}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            if (previewAudioUrl) URL.revokeObjectURL(previewAudioUrl);
                            setRecordedAudio(null);
                            setRecordingTime(0);
                            setPreviewAudioUrl(null);
                            setReplacingVtId(null);
                            setSegueData(null);
                          }}
                          sx={{ color: '#ff6b6b', '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' } }}
                        >
                          Record Again
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Segue Editor for the freshly recorded voicetrack */}
          {!isMixblockMarker && recordedAudio && previewAudioUrl && (immediatePrevious || immediateNext) && (
            <Card sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid #3a4456' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GraphicEq sx={{ color: '#c060ff' }} />
                  <Typography variant="subtitle2" sx={{ color: '#c060ff' }}>
                    Segue &amp; Mix
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                  The voicetrack starts on the previous track’s outro marker. Drag it later to leave more of the outro uncovered. These
                  settings are saved with the voicetrack.
                </Typography>
                <VoicetrackSegueEditor
                  apiUrl={apiUrl}
                  previousItem={immediatePrevious}
                  nextItem={immediateNext}
                  vtAudioUrl={previewAudioUrl}
                  vtDuration={recordingTime}
                  initialSegue={segueData}
                  sweepOverIntro={useAsSweeper}
                  musicBed={musicBedEnabled ? {
                    enabled: true,
                    mode: musicBedSelectionMode,
                    assetId: musicBedSelectionMode === 'specific' && musicBedAssetId ? musicBedAssetId : null,
                    volumeDb: musicBedVolume,
                  } : null}
                  onChange={setSegueData}
                />
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'var(--rs-paper)', borderTop: '1px solid var(--rs-border)' }}>
        <Button onClick={onClose} sx={{ color: '#999' }}>
          Discard
        </Button>
        
        {/* Save for specific region when in split mode */}
        {regions.length > 0 && activeRegionTab > 0 && (
          <Button
            onClick={() => handleSaveForRegion(regions[activeRegionTab - 1]?.id)}
            disabled={!recordedAudio}
            variant="outlined"
            sx={{
              borderColor: '#22d3ee',
              color: '#22d3ee',
              '&:disabled': { borderColor: '#3a4456', color: '#3a4456' }
            }}
          >
            Save for {regions[activeRegionTab - 1]?.name || 'Region'}
          </Button>
        )}
        
        <Button
          onClick={handleSave}
          disabled={!recordedAudio}
          variant="contained"
          sx={{
            bgcolor: '#9c27b0',
            '&:hover': { bgcolor: '#7b1fa2' },
            '&:disabled': { bgcolor: '#3a4456' }
          }}
        >
          {regions.length > 0 && activeRegionTab === 0 ? 'Save Shared VT' : 'Save Voicetrack'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Segue editor for an already-saved voicetrack */}
    <Dialog
      open={!!editingSegueVt && !isMixblockMarker}
      onClose={closeSegueEditorForSaved}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'var(--rs-paper)', color: '#c060ff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEq sx={{ color: '#c060ff' }} />
        Edit Segue &amp; Volumes
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
            The voicetrack starts on the previous outro marker. Drag it to fine-tune, preview, then save.
          </Typography>
          {editingSegueVt && editingSegueAudioUrl && (
            <VoicetrackSegueEditor
              apiUrl={apiUrl}
              previousItem={immediatePrevious}
              nextItem={immediateNext}
              vtAudioUrl={editingSegueAudioUrl}
              vtDuration={editingSegueVt.duration || 0}
              initialSegue={parseSavedSegue(editingSegueVt)}
              sweepOverIntro={!!parseSavedSegue(editingSegueVt)?.sweep_over_intro}
              onChange={(s) => { editingSegueDraftRef.current = s; }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'var(--rs-paper)', borderTop: '1px solid var(--rs-border)' }}>
        <Button onClick={closeSegueEditorForSaved} sx={{ color: '#999' }} disabled={savingSegue}>
          Cancel
        </Button>
        <Button
          onClick={() => saveSegueForSaved(editingSegueDraftRef.current)}
          variant="contained"
          disabled={savingSegue}
          sx={{ bgcolor: '#c060ff', '&:hover': { bgcolor: '#a040e0' } }}
        >
          {savingSegue ? 'Saving…' : 'Save Segue'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
